import { format as formatDate } from "date-fns";
import { Calendar as CalendarIcon, Check, ChevronDown, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { Calendar } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  StatCardsSection,
  RecentActivityTable,
  RequestsByTypeSection,
  TeamPerformanceSection,
  TeamFilterSelect,
} from "@/components/UnderwritingDashboard";
import { TypeFilter } from "@/components/UnderwritingDashboard/RecentActivityTable";
import { underwritingService } from "@/services/UnderwritingService";
import { useAppData } from "@/context/AppDataContext";
import { useApi } from "@/hooks/useApi";
import { useTeamFilter } from "@/hooks/useTeamFilter";
import {
  UWDashboardStatsResponse,
  UnderwritingActivity,
  BusinessFilter,
  QueuePeriod,
  QueueCustomRange,
} from "@/models/underwriting";
import {
  computeQueuePeriodRange,
  businessToChannel,
} from "@/helpers/underwritingDashboard";

const BUSINESS_OPTIONS: BusinessFilter[] = ["Whole Business", "Consumer Direct", "Dealer Direct"];

const PERIOD_OPTIONS: { value: QueuePeriod; label: string }[] = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 days" },
  { value: "Last30Days", label: "Last 30 days" },
  { value: "Custom", label: "Custom range" },
];

const OPEN_STATUSES = new Set(["Pending", "InReview"]);

function formatUpdatedAgo(seconds: number): string {
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ago`;
}

export function UnderwritingDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAppData();
  const teamFilter = useTeamFilter();

  const initialTypeFilter: TypeFilter =
    (location.state as { typeFilter?: TypeFilter } | null)?.typeFilter ?? "all";

  const [typeFilter] = useState<TypeFilter>(initialTypeFilter);
  const [business, setBusiness] = useState<BusinessFilter>("Whole Business");
  const [period, setPeriod] = useState<QueuePeriod>("Last7Days");
  const [customRange, setCustomRange] = useState<QueueCustomRange | null>(null);
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const { start: periodStart, end: periodEnd } = useMemo(
    () => computeQueuePeriodRange(period, customRange),
    [period, customRange]
  );

  const statsResult = useApi<UWDashboardStatsResponse>(
    (signal) => underwritingService.getDashboardStats(`${periodStart}T00:00:00`, `${periodEnd}T23:59:59`, signal),
    [periodStart, periodEnd, refreshKey]
  );

  // Fetch the period's open requests, then filter by Business/type on the client.
  const activityResult = useApi<UnderwritingActivity[]>(
    (signal) =>
      underwritingService.getRecentActivity(
        {
          periodStart: `${periodStart}T00:00:00`,
          periodEnd: `${periodEnd}T23:59:59`,
        },
        signal
      ),
    [periodStart, periodEnd, refreshKey]
  );

  const channel = businessToChannel(business);

  const activities = useMemo(() => {
    let rows = activityResult.data ?? [];
    rows = rows.filter((a) => OPEN_STATUSES.has(a.status));
    if (channel) rows = rows.filter((a) => a.channel === channel);
    if (typeFilter !== "all") rows = rows.filter((a) => a.requestType === typeFilter);
    rows = teamFilter.filterActivities(rows);
    return rows;
  }, [activityResult.data, channel, typeFilter, teamFilter]);

  const stats = statsResult.data;

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleClaim = useCallback(
    async (activity: UnderwritingActivity) => {
      if (!currentUser?.name) return;
      await underwritingService.claimRequest(activity, currentUser.name);
      navigate(`/underwriting-queue/${activity.applicationId}`, {
        state: { activity, justClaimed: true, origin: { path: "/underwriting-queue", label: "Underwriting Queue" } },
      });
    },
    [currentUser, navigate]
  );

  const isAnyLoading = statsResult.loading || activityResult.loading;

  // ─── Auto-refresh every 3 minutes + "Updated x ago" indicator ───────────────
  const AUTO_REFRESH_SECS = 180;
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      if (periodPopoverOpen) return;
      setNow(Date.now());
      if ((Date.now() - lastUpdated) / 1000 >= AUTO_REFRESH_SECS) {
        handleRefresh();
        setLastUpdated(Date.now());
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [handleRefresh, lastUpdated, periodPopoverOpen]);

  const handleManualRefresh = useCallback(() => {
    handleRefresh();
    setLastUpdated(Date.now());
    setNow(Date.now());
  }, [handleRefresh]);

  const updatedLabel = formatUpdatedAgo(Math.floor((now - lastUpdated) / 1000));
  const periodLabel = useMemo(() => {
    if (period !== "Custom" || !customRange) {
      return PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? "Last 7 days";
    }
    const from = new Date(`${customRange.start}T00:00:00`);
    const to = new Date(`${customRange.end}T00:00:00`);
    return `${formatDate(from, "dd MMM")} - ${formatDate(to, "dd MMM")}`;
  }, [period, customRange]);

  const handlePeriodChange = (nextPeriod: QueuePeriod) => {
    if (nextPeriod === "Custom") {
      setPeriod("Custom");
      if (customRange) {
        setDraftCustomRange({
          from: new Date(`${customRange.start}T00:00:00`),
          to: new Date(`${customRange.end}T00:00:00`),
        });
      }
      setShowCustomCalendar(true);
      setPeriodPopoverOpen(true);
      return;
    }

    setPeriod(nextPeriod);
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  const handleCustomApply = () => {
    if (!draftCustomRange?.from || !draftCustomRange?.to) return;
    const from = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.from : draftCustomRange.to;
    const to = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.to : draftCustomRange.from;

    setCustomRange({
      start: formatDate(from, "yyyy-MM-dd"),
      end: formatDate(to, "yyyy-MM-dd"),
    });
    setPeriod("Custom");
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  return (
    <div className="flex h-full flex-col bg-zinc-100">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <span className="text-sm font-semibold text-foreground">Underwriting Queue</span>

        <div className="flex items-center gap-2">
          {/* Business filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <span className="text-muted-foreground">Business:</span>
                <span className="font-medium">{business}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {BUSINESS_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt} onClick={() => setBusiness(opt)} className="justify-between">
                  <span className={business === opt ? "font-semibold" : ""}>{opt}</span>
                  {business === opt && <Check className="size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Team filter (multi-select, grouped by Head of Sales) */}
          <TeamFilterSelect teamFilter={teamFilter} />

          {/* Date period filter (stats + queue) */}
          <Popover
            open={periodPopoverOpen}
            onOpenChange={setPeriodPopoverOpen}
          >
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <CalendarIcon className="size-3.5 text-muted-foreground" />
                <span className="font-medium">{periodLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              {!showCustomCalendar ? (
                <div className="w-40 p-1">
                  {PERIOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handlePeriodChange(opt.value)}
                      className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                    >
                      <span className={period === opt.value ? "font-semibold" : ""}>{opt.label}</span>
                      {period === opt.value && <Check className="size-4" />}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div className="border-b px-3 py-2 text-sm font-medium text-foreground">Select date range</div>
                  <Calendar
                    mode="range"
                    selected={draftCustomRange}
                    onSelect={(range) => setDraftCustomRange(range)}
                    numberOfMonths={1}
                    captionLayout="dropdown"
                  />
                  <div className="flex items-center justify-end gap-2 border-t p-3">
                    <button
                      className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      onClick={() => {
                        setShowCustomCalendar(false);
                        setPeriodPopoverOpen(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                      disabled={!draftCustomRange?.from || !draftCustomRange?.to}
                      onClick={handleCustomApply}
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>

          {/* Refresh */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={isAnyLoading}
              className="flex items-center justify-center rounded-md border border-border bg-card p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw className={`size-4 ${isAnyLoading ? "animate-spin" : ""}`} />
            </button>
            <span className="text-[11px] leading-tight text-muted-foreground">
              Updated
              <br />
              <span className="font-medium text-emerald-600">{updatedLabel}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto [scrollbar-gutter:stable]">
        {/* Error banners */}
        {(statsResult.error || activityResult.error) && (
          <div className="mx-6 mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            {statsResult.error ?? activityResult.error}
          </div>
        )}

        {/* Content */}
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-6 py-5">
          <h1 className="text-[22px] font-semibold text-foreground">Underwriting Queue</h1>
          {/* Stat cards */}
          {statsResult.loading && !stats ? (
            <div className="flex h-28 items-center justify-center">
              <Spinner />
            </div>
          ) : stats ? (
            <StatCardsSection stats={stats} />
          ) : null}

          {/* Team Performance */}
          <TeamPerformanceSection start={periodStart} end={periodEnd} business={business} refreshKey={refreshKey} />

          {/* Requests by Type */}
          <RequestsByTypeSection start={periodStart} end={periodEnd} business={business} refreshKey={refreshKey} />
        </div>

        {/* Open Requests — full-width white band, content still constrained to the 1200px column */}
        <div className="w-full bg-white">
          <div className="mx-auto w-full max-w-[1200px] px-6 py-5">
            <RecentActivityTable
              activities={activities}
              onRowClick={(row) =>
                navigate(`/underwriting-queue/${row.applicationId}`, {
                  state: { activity: row, origin: { path: "/underwriting-queue", label: "Underwriting Queue" } },
                })
              }
              currentUserName={currentUser?.name ?? null}
              onClaim={handleClaim}
              onViewAll={() =>
                navigate("/underwriting-queue/all-requests", { state: { business, queuePeriod: period, queueCustomRange: customRange } })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
